import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Text,
  Flex,
  useToast,
  VStack,
  HStack,
  IconButton,
  Image,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '@chakra-ui/react';
import Card from '../../components/Card';
import ModalComponent from '../../components/Modal';
import AlertModalOverlay from '../../components/AlertModalComponent';
import Label from '../../components/Label';
import ServerSideDatatable from '../../components/ServerSideDatatable';
import DeleteBlueIcon from '../../assets/images/delete-blue-icon.png';
import AddIcon from '../../assets/images/add.png';
import {
  PAGE_TITLE,
  TOAST_SETTINGS,
  DATE_TO_SHOW_WITH_FORMAT,
  DATE_SAVE_TO_DB_WITH_FORMAT,
} from '../../constants';
import StyledTaxRegionContainer from './TaxRegion.styled';
import ComplianceRulesForm from '../../components/ComplianceRulesForm';
import {
  getTaxRegions,
  getComplianceRulesByRegion,
  createComplianceRule,
  updateComplianceRule,
  deleteComplianceRule,
} from '../../redux/action/taxRegion.action';

const TaxRegion = function () {
  const toast = useToast();
  const { user_details } = useSelector((state) => state.authentication);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedState, setSelectedState] = useState(null);
  const [isRuleModalOpen, setIsRuleModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteRuleId, setDeleteRuleId] = useState(null);
  const [stateRules, setStateRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingRuleId, setLoadingRuleId] = useState(null);
  const [deletingRuleId, setDeletingRuleId] = useState(null);
  const [refreshData, setRefreshData] = useState(false);
  const [isStateLoading, setIsStateLoading] = useState(false);

  useEffect(() => {
    document.title = PAGE_TITLE.TAX_REGION;
  }, []);

  // Pagination function for ServerSideDatatable - calls API
  const handlePagination = async (pageSize, pageNumber, sortKey, sortOrder) => {
    try {
      setIsStateLoading(true);
      const response = await getTaxRegions(pageSize, pageNumber, sortKey, sortOrder);

      if (response.ok) {
        const apiData = response.data.data;

        return {
          data: {
            tax_regions: apiData,
            count: response.data.count,
            total_pages: response.data.total_pages,
          },
        };
      }

      toast({
        title: 'Error',
        description: response.error || 'Failed to fetch tax regions',
        status: TOAST_SETTINGS.ERROR,
        ...TOAST_SETTINGS.SETTINGS,
      });

      return {
        data: {
          result: [],
          count: 0,
        },
      };
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An error occurred while fetching tax regions',
        status: TOAST_SETTINGS.ERROR,
        ...TOAST_SETTINGS.SETTINGS,
      });

      return {
        data: {
          result: [],
          count: 0,
        },
      };
    } finally {
      setIsStateLoading(false);
    }
  };

  const handleRuleCountClick = (state) => {
    setSelectedState(state);
    setLoading(true);
    setIsModalOpen(true);

    // Fetch compliance rules after modal is opened
    (async () => {
      try {
        const response = await getComplianceRulesByRegion(state.tax_region_id);

        if (response.ok) {
          setStateRules(response.data.rules || []);
        } else {
          toast({
            title: 'Error',
            description: response.error || 'Failed to fetch compliance rules',
            status: TOAST_SETTINGS.ERROR,
            ...TOAST_SETTINGS.SETTINGS,
          });
          setStateRules([]);
        }
      } catch (error) {
        toast({
          title: 'Error',
          description: 'An error occurred while fetching compliance rules',
          status: TOAST_SETTINGS.ERROR,
          ...TOAST_SETTINGS.SETTINGS,
        });
        setStateRules([]);
      } finally {
        setLoading(false);
      }
    })();
  };

  // Render function for compliance rules column (moved outside to avoid ESLint warning)
  const renderTaxRegionColumn = (count, row) => {
    const ruleText = `${count} ${count <= 1 ? 'Rule' : 'Rules'}`;

    return (
      <Flex align="center">
        <Image
          mr="10px"
          cursor="pointer"
          src={AddIcon}
          height={6}
          onClick={() => handleRuleCountClick(row)}
        />
        <Text fontSize="lg" fontWeight="bold" color="blue.600">
          {ruleText}
        </Text>
      </Flex>
    );
  };

  // Table columns configuration
  const [columns] = useState([
    {
      label: 'State',
      field: 'name',
      sort: true,
    },
    {
      label: 'Compliance Rules',
      field: 'rulesCount',
      sort: false,
      customRow: renderTaxRegionColumn,
    },
  ]);

  // Handle modal close
  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedState(null);
    setStateRules([]);
  };

  // Handle add new rule
  const handleAddRule = () => {
    setIsEditMode(false);
    setSelectedRule(null);
    setIsRuleModalOpen(true);
  };

  // Handle edit rule
  const handleEditRule = (rule) => {
    setIsEditMode(true);
    setSelectedRule(rule);
    setIsRuleModalOpen(true);
  };

  // Handle delete rule
  const handleDeleteRule = (ruleId) => {
    setDeleteRuleId(ruleId);
    setIsDeleteModalOpen(true);
  };

  // Confirm delete rule
  const confirmDeleteRule = async () => {
    try {
      // Set loading state for delete
      setDeletingRuleId(deleteRuleId);
      setIsDeleteModalOpen(false);

      // Call API to delete compliance rule
      const response = await deleteComplianceRule(selectedState.tax_region_id, deleteRuleId);

      if (response.ok) {
        toast({
          title: 'Success',
          description: response.data.message || 'Compliance rule deleted successfully.',
          status: TOAST_SETTINGS.SUCCESS,
          ...TOAST_SETTINGS.SETTINGS,
        });

        // Refresh the rules list
        setLoading(true);
        const refreshResponse = await getComplianceRulesByRegion(selectedState.tax_region_id);
        if (refreshResponse.ok) {
          setStateRules(refreshResponse.data.rules || []);
        }
        setLoading(false);

        setDeleteRuleId(null);
        setDeletingRuleId(null);
      } else {
        toast({
          title: 'Error',
          description: response.error || 'Failed to delete compliance rule.',
          status: TOAST_SETTINGS.ERROR,
          ...TOAST_SETTINGS.SETTINGS,
        });
        setDeletingRuleId(null);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An error occurred while deleting the compliance rule.',
        status: TOAST_SETTINGS.ERROR,
        ...TOAST_SETTINGS.SETTINGS,
      });
      setDeletingRuleId(null);
    }
    setRefreshData(!refreshData);
  };

  // Handle rule save (create or update)
  const handleRuleSave = async (ruleData) => {
    try {
      // Set loading state for edit mode
      if (isEditMode && selectedRule?.id) {
        setLoadingRuleId(selectedRule.id);
      }

      // Transform form data to API payload format
      const payload = {
        start_date: DATE_SAVE_TO_DB_WITH_FORMAT(ruleData.startDate, user_details.date_format),
        max_weekly_hours: parseFloat(ruleData.workingHours),
        house_occupancy_limit: parseInt(ruleData.multipleOccupancy, 10),
        max_consecutive_days: parseInt(ruleData.consecutiveDays, 10),
        duplication_bank_details: ruleData.duplicateBankAccounts,
      };

      let response;

      if (isEditMode && selectedRule?.id) {
        // Call API to update compliance rule
        response = await updateComplianceRule(
          selectedState.tax_region_id,
          selectedRule.id,
          payload
        );
      } else {
        // Call API to create compliance rule
        response = await createComplianceRule(selectedState.tax_region_id, payload);
        setRefreshData(!refreshData);
      }

      if (response.ok) {
        toast({
          title: 'Success',
          description:
            response.data.message ||
            `Compliance rule ${isEditMode ? 'updated' : 'created'} successfully.`,
          status: TOAST_SETTINGS.SUCCESS,
          ...TOAST_SETTINGS.SETTINGS,
        });

        // Refresh the rules list
        setLoading(true);
        const refreshResponse = await getComplianceRulesByRegion(selectedState.tax_region_id);
        if (refreshResponse.ok) {
          setStateRules(refreshResponse.data.rules || []);
        }
        setLoading(false);

        setIsRuleModalOpen(false);
        setSelectedRule(null);
        setIsEditMode(false);
        setLoadingRuleId(null);
      } else {
        toast({
          title: 'Error',
          description:
            response.error || `Failed to ${isEditMode ? 'update' : 'create'} compliance rule.`,
          status: TOAST_SETTINGS.ERROR,
          ...TOAST_SETTINGS.SETTINGS,
        });
        setLoadingRuleId(null);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: `An error occurred while ${
          isEditMode ? 'updating' : 'creating'
        } the compliance rule.`,
        status: TOAST_SETTINGS.ERROR,
        ...TOAST_SETTINGS.SETTINGS,
      });
      setLoadingRuleId(null);
    }
  };

  return (
    <StyledTaxRegionContainer bg="main.secondary">
      <Card>
        <Flex justifyContent="space-between" alignItems="center" mb={6}>
          <Text fontSize="2xl" fontWeight="bold" color="main.primary">
            Tax Region Management
          </Text>
        </Flex>

        <ServerSideDatatable
          pageSize={10}
          initialSortKey="name"
          column={columns}
          onPaginate={handlePagination}
          pagination
          pages={[5, 10, 15]}
          dataKey="tax_regions"
          refresh={refreshData}
          isLoading={isStateLoading && !isModalOpen}
        />
      </Card>

      {/* Modal for managing rules */}
      <ModalComponent
        specifySize="6xl"
        isOpen={isModalOpen}
        onClose={handleModalClose}
        modalTitle={`Manage Compliance Rules - ${selectedState?.name || ''}`}
        modalContent={
          <VStack spacing={4} align="stretch">
            <Flex mb="10px" justify="space-between" align="center">
              <Flex cursor="pointer" alignItems="center" onClick={handleAddRule}>
                <Image mr="10px" src={AddIcon} height={6} />
                <Label label="Add Rule" color="main.primary" />
              </Flex>
            </Flex>
            <Box overflowX="auto">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th px={2} py={2} fontWeight="bold" color="blue.600">
                      Start Date
                    </Th>
                    <Th px={2} py={2} fontWeight="bold" color="blue.600">
                      Duplication Bank Accounts
                    </Th>
                    <Th px={2} py={2} fontWeight="bold" color="blue.600">
                      Working Hours
                    </Th>
                    <Th px={2} py={2} fontWeight="bold" color="blue.600">
                      House Occupancy
                    </Th>
                    <Th px={2} py={2} fontWeight="bold" color="blue.600">
                      Consecutive Days
                    </Th>
                    <Th px={2} py={2} fontWeight="bold" color="blue.600">
                      Actions
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {loading ? (
                    <Tr>
                      <Td colSpan="7">
                        <Flex justify="center" align="center">
                          <Spinner size="md" />
                        </Flex>
                      </Td>
                    </Tr>
                  ) : stateRules.length === 0 ? (
                    <Tr>
                      <Td colSpan="7">
                        <Text align="center">
                          No Rules Available. Click the + icon to add a new rule.
                        </Text>
                      </Td>
                    </Tr>
                  ) : (
                    stateRules.map((rule) => (
                      <Tr key={rule.id}>
                        <Td px={2} py={2}>
                          {rule.start_date
                            ? DATE_TO_SHOW_WITH_FORMAT(rule.start_date, user_details.date_format)
                            : '-'}
                        </Td>
                        <Td px={2} py={2}>
                          <Text
                            color={rule.duplication_bank_details ? 'green.600' : 'red.600'}
                            fontWeight="medium"
                          >
                            {rule.duplication_bank_details ? 'Yes' : 'No'}
                          </Text>
                        </Td>
                        <Td px={2} py={2}>
                          {rule.max_weekly_hours || '-'}
                        </Td>
                        <Td px={2} py={2}>
                          {rule.house_occupancy_limit || '-'}
                        </Td>
                        <Td px={2} py={2}>
                          {rule.max_consecutive_days || '-'}
                        </Td>
                        <Td px={2} py={2}>
                          <HStack spacing={2}>
                            <Box position="relative" display="inline-block">
                              <IconButton
                                size="sm"
                                aria-label="Delete rule"
                                icon={<Image src={DeleteBlueIcon} height={4} />}
                                variant="ghost"
                                onClick={() => handleDeleteRule(rule.id)}
                                isDisabled={deletingRuleId === rule.id}
                              />
                              {deletingRuleId === rule.id && (
                                <Flex
                                  position="absolute"
                                  top={0}
                                  left={0}
                                  right={0}
                                  bottom={0}
                                  justify="center"
                                  align="center"
                                  borderRadius="md"
                                  bg="rgba(255, 255, 255, 0.7)"
                                >
                                  <Spinner size="sm" />
                                </Flex>
                              )}
                            </Box>
                            <Box position="relative" display="inline-block">
                              <Button
                                size="sm"
                                onClick={() => handleEditRule(rule)}
                                isDisabled={loadingRuleId === rule.id}
                              >
                                Edit
                              </Button>
                              {loadingRuleId === rule.id && (
                                <Flex
                                  position="absolute"
                                  top={0}
                                  left={0}
                                  right={0}
                                  bottom={0}
                                  justify="center"
                                  align="center"
                                  borderRadius="md"
                                  bg="rgba(255, 255, 255, 0.7)"
                                >
                                  <Spinner size="sm" />
                                </Flex>
                              )}
                            </Box>
                          </HStack>
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </Box>
          </VStack>
        }
      />

      {/* Add/Edit Rule Modal */}
      <ComplianceRulesForm
        isOpen={isRuleModalOpen}
        onClose={() => setIsRuleModalOpen(false)}
        onFormSubmit={handleRuleSave}
        isEditMode={isEditMode}
        existingData={selectedRule}
        defaultDate={stateRules.length > 0 ? stateRules[stateRules.length - 1]?.start_date : null}
      />
      {/* Delete Confirmation Modal */}
      <AlertModalOverlay
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Compliance Rule"
        body={
          <Text fontSize="md">
            Are you sure you want to delete this compliance rule? This action cannot be undone.
          </Text>
        }
        negative={
          <Button
            onClick={() => setIsDeleteModalOpen(false)}
            bg="main.secondary"
            mr={3}
            _hover={{ bg: 'main.secondary' }}
            color="main.black"
          >
            Cancel
          </Button>
        }
        positive={
          <Button onClick={confirmDeleteRule} colorScheme="red" ml={3}>
            Delete
          </Button>
        }
      />
    </StyledTaxRegionContainer>
  );
};

export default TaxRegion;
